/**
 * Data Saver Utility
 * Sends data directly to backend at submit time. No cache for persistence.
 */

import { getApiBaseUrl } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';
import { fetchWithRetry } from './fetchWithRetry';
import { TIMEOUT_MS, logSkinResilience } from './skinResilience';
import { recordSkinTelemetry } from './skinTelemetry';

const API_BASE_URL = (getApiBaseUrl() || '').replace(/\/$/, '');

/** Build contact + user payload (snake_case) from form/state data (camelCase or snake_case) */
function toBackendUserContact(userOrContact = {}) {
  return {
    name: userOrContact.name ?? '',
    mobile_number: userOrContact.mobile_number ?? userOrContact.mobileNumber ?? '',
    otp_verified: userOrContact.otp_verified,
    email: userOrContact.email ?? null,
    instagram_id: userOrContact.instagram_id ?? userOrContact.instagramId ?? null,
    gender: userOrContact.gender ?? null,
    skin_type: userOrContact.skin_type ?? userOrContact.skinType ?? null,
    age: userOrContact.age ?? null
  };
}

const SESSION_STORAGE_KEY = 'skin_detection_session_id';
const PENDING_SAVE_KEY = 'skin_pending_save_analysis_v1';
const PENDING_FLUSH_DELAY_MS = 5000;
const MAX_PENDING_FLUSH_ATTEMPTS = 5;

let _pendingBySession = null;
let _flushTimer = null;
const _flushInFlight = new Map();

function _loadPending() {
  if (_pendingBySession) return _pendingBySession;
  try {
    const raw = localStorage.getItem(PENDING_SAVE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    _pendingBySession = parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    _pendingBySession = {};
  }
  return _pendingBySession;
}

function _persistPending() {
  try {
    localStorage.setItem(PENDING_SAVE_KEY, JSON.stringify(_pendingBySession || {}));
  } catch (_) {}
}

function _mergeIntoPending(sessionId, patch) {
  if (sessionId == null || String(sessionId).trim() === '') return;
  const sid = String(sessionId).trim();
  const pending = _loadPending();
  const prev = (pending[sid] && typeof pending[sid] === 'object') ? pending[sid] : {};
  const { _meta, ...restPrev } = prev;
  pending[sid] = {
    ...restPrev,
    ...patch,
    _meta: {
      ...( _meta && typeof _meta === 'object' ? _meta : {}),
      updated_at: new Date().toISOString(),
    },
  };
  _persistPending();
}

function _recordPendingFlushFailure(sessionId, errorMessage) {
  const sid = String(sessionId).trim();
  const pending = _loadPending();
  const staged = pending[sid];
  if (!staged) return;
  const meta = staged._meta && typeof staged._meta === 'object' ? staged._meta : {};
  pending[sid] = {
    ...staged,
    _meta: {
      ...meta,
      retry_count: Number(meta.retry_count || 0) + 1,
      last_error: errorMessage || null,
      last_attempt_at: new Date().toISOString(),
    },
  };
  _persistPending();
  recordSkinTelemetry('save_analysis_retry', { sessionId: sid, message: errorMessage });
}

function _clearPending(sessionId) {
  const sid = sessionId == null ? '' : String(sessionId).trim();
  if (!sid) return;
  const pending = _loadPending();
  if (pending[sid]) {
    delete pending[sid];
    _persistPending();
  }
}

function _scheduleSafetyFlush(sessionId, appSessionToken = null) {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    flushPendingSaveAnalysis(sessionId, appSessionToken).catch(() => {});
  }, PENDING_FLUSH_DELAY_MS);
}

export function buildEnvironmentalAnalyticsFields(aqiData = null) {
  if (!aqiData || !aqiData.success) return {};
  return {
    uv_index: aqiData.uv_index ?? null,
    temperature: aqiData.temperature ?? null,
    humidity: aqiData.humidity ?? null,
    wind_speed: aqiData.wind_speed ?? null,
    location: aqiData.location ?? null,
    latitude: aqiData.latitude ?? null,
    longitude: aqiData.longitude ?? null,
  };
}

async function saveToBackend(saveData, appSessionToken = null, options = {}) {
  const { signal, onRetry } = options;
  const payload = { ...saveData };
  delete payload._meta;
  if (appSessionToken && String(appSessionToken).trim()) {
    payload.app_session_token = String(appSessionToken).trim();
  }
  const response = await fetchWithRetry(`${API_BASE_URL}/api/save-analysis`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS,
    signal,
  }, {
    label: 'save-analysis',
    onRetry,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const error = new Error(err.error || 'Failed to save data');
    error.status = response.status;
    error.retryable = Boolean(err.retryable);
    throw error;
  }
  const data = await response.json();
  if (data?.persistence_verified === false) {
    const error = new Error('Analysis saved but persistence verification failed');
    error.retryable = true;
    throw error;
  }
  // Adopt skin_id from response so subsequent requests use it (instead of UUID)
  if (data && data.skin_id != null) {
    const skinIdStr = String(data.skin_id);
    if (localStorage.getItem(SESSION_STORAGE_KEY) !== skinIdStr) {
      localStorage.setItem(SESSION_STORAGE_KEY, skinIdStr);
    }
  }
  return data;
}

/** Record a flow-stage flag in skin_users_flow_minimal only (no product/cart IDs). */
export async function recordSkinFlowStage(sessionId, stage, appSessionToken = null, metadata = null) {
  const eventData = { stage: String(stage) };
  if (metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0) {
    eventData.event_metadata = metadata;
  }
  return saveSkinAnalyticsEvent(sessionId, eventData, appSessionToken);
}

export async function saveSkinAnalyticsEvent(sessionId, eventData = {}, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '') return { success: false, error: 'Session/skin ID is required' };
  try {
    const sid = String(sessionId).trim();
    const stage = eventData?.event_type || eventData?.step_name || eventData?.stage || 'event';
    const payload = { ...eventData };
    // Remove top-level keys we already use to avoid duplication
    delete payload.event_type;
    delete payload.step_name;
    delete payload.stage;

    const body = {
      skin_id: sid,
      session_id: sid,
      stage: String(stage),
      payload: Object.keys(payload).length ? payload : null,
      event_time: new Date().toISOString(),
    };
    if (appSessionToken && String(appSessionToken).trim()) body.app_session_token = String(appSessionToken).trim();

    // Fire-and-forget is fine but return the result when available
    const resp = await fetchWithTimeout(`${API_BASE_URL}/api/record-flow-stage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      timeoutMs: 6000,
    }).catch(() => null);
    if (!resp) return { success: false, skipped: true };
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      return { success: false, error: err.error || 'Failed to record event' };
    }
    const d = await resp.json().catch(() => ({}));
    return { success: true, event_id: d?.event_id };
  } catch (e) {
    return { success: false, error: e?.message || 'internal' };
  }
}

/** Extract only scores from skin conditions for payload size */
function extractScoresOnly(skinConditions) {
  if (!skinConditions || typeof skinConditions !== 'object') return null;
  const keys = ['hydration', 'oxygen', 'smoothness', 'dullness', 'uneven_skintone'];
  const out = {};
  keys.forEach(k => {
    if (skinConditions[k] && typeof skinConditions[k] === 'object')
      out[k] = { score: skinConditions[k].score ?? null };
  });
  return Object.keys(out).length ? out : null;
}

function normalizeSideResults(sideResults) {
  if (!Array.isArray(sideResults)) return [];
  return sideResults
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const key = String(item.key || item.view || '').trim().toLowerCase();
      if (!key) return null;
      return {
        ...item,
        key,
        detections: Array.isArray(item.detections) ? item.detections : [],
      };
    })
    .filter(Boolean);
}

function buildDetectionSkinDataPayload(skinResults = {}) {
  const frontDetections = Array.isArray(skinResults.frontDetections)
    ? skinResults.frontDetections
    : Array.isArray(skinResults.front_detections)
      ? skinResults.front_detections
      : (Array.isArray(skinResults.detections) ? skinResults.detections : []);

  const sideResults = normalizeSideResults(skinResults.sideResults || skinResults.side_results);
  const leftDetections = (sideResults.find((item) => item.key === 'left')?.detections) || [];
  const rightDetections = (sideResults.find((item) => item.key === 'right')?.detections) || [];

  return {
    detections: Array.isArray(skinResults.detections) ? skinResults.detections : [],
    front_detections: frontDetections,
    side_results: sideResults,
    bounding_boxes: {
      front: frontDetections,
      left: leftDetections,
      right: rightDetections,
    },
    skinScore: skinResults.skinScore ?? null,
    skinConditions: extractScoresOnly(skinResults.skinConditions),
  };
}

/** Build payload identifier - session_id and skin_id (backend resolves both) */
function toIdentifier(sessionId) {
  if (!sessionId) return {};
  const s = String(sessionId).trim();
  return { session_id: s, skin_id: s };
}

export async function flushPendingSaveAnalysis(sessionId, appSessionToken = null, options = {}) {
  if (sessionId == null || String(sessionId).trim() === '') return { success: false, error: 'Session/skin ID is required' };
  const sid = String(sessionId).trim();

  if (_flushInFlight.has(sid)) {
    return _flushInFlight.get(sid);
  }

  const run = (async () => {
    const pending = _loadPending();
    const staged = pending[sid];
    if (!staged || typeof staged !== 'object' || Object.keys(staged).length === 0) {
      return { success: true, skipped: true };
    }
    const meta = staged._meta && typeof staged._meta === 'object' ? staged._meta : {};
    if (Number(meta.retry_count || 0) >= MAX_PENDING_FLUSH_ATTEMPTS) {
      logSkinResilience('retry_exhausted', { label: 'save-analysis-pending', sessionId: sid, attempts: meta.retry_count });
      return { success: false, error: 'Pending save retries exhausted', exhausted: true };
    }
    try {
      const result = await saveToBackend({ ...toIdentifier(sid), ...staged }, appSessionToken, options);
      _clearPending(sid);
      return {
        success: true,
        persistence_verified: result?.persistence_verified,
      };
    } catch (e) {
      _recordPendingFlushFailure(sid, e.message);
      logSkinResilience('retry', { label: 'save-analysis-pending', sessionId: sid, message: e.message });
      return { success: false, error: e.message };
    }
  })();

  _flushInFlight.set(sid, run);
  try {
    return await run;
  } finally {
    _flushInFlight.delete(sid);
  }
}

/** Retry any persisted pending saves (e.g. after reconnect). */
export async function flushAllPendingSaveAnalysis(appSessionToken = null) {
  const pending = _loadPending();
  const sessionIds = Object.keys(pending || {});
  const results = await Promise.all(
    sessionIds.map((sid) => flushPendingSaveAnalysis(sid, appSessionToken).catch((e) => ({ success: false, error: e.message })))
  );
  return results;
}

if (typeof window !== 'undefined' && !window.__skinPendingOnlineFlush) {
  window.__skinPendingOnlineFlush = true;
  window.addEventListener('online', () => {
    logSkinResilience('reconnect', { label: 'save-analysis-pending-flush' });
    flushAllPendingSaveAnalysis().catch(() => {});
  });
}

/**
 * Save only device_info for an existing session (e.g. right after creating session on landing CTA).
 * @param {string} sessionId - session_id / skin_id from backend
 * @param {object} deviceInfo - device info from captureDeviceInfo()
 */
export async function saveDeviceInfo(sessionId, deviceInfo, appSessionToken = null) {
  if (!sessionId || String(sessionId).trim() === '') return { success: false, error: 'Session ID required' };
  if (!deviceInfo || typeof deviceInfo !== 'object' || Object.keys(deviceInfo).length === 0) return { success: true };
  try {
    await saveToBackend({ ...toIdentifier(sessionId), device_info: deviceInfo }, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save user info (gender, skin_type, age) directly to backend. No cache. Does not send contact fields.
 * @param {string|number} sessionId - session_id or skin_id (numeric string)
 * @param {object} userData - { gender, skinType, age, ... }
 * @param {object} [deviceInfo] - optional device info captured at landing CTA (user_agent, platform, device_type, etc.)
 */
export async function saveUserInfo(sessionId, userData, deviceInfo = null, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const payload = {
      ...toIdentifier(sessionId),
      gender: userData.gender ?? null,
      skin_type: userData.skin_type ?? userData.skinType ?? null,
      age: userData.age ?? null
    };
    if (deviceInfo && typeof deviceInfo === 'object' && Object.keys(deviceInfo).length > 0) {
      payload.device_info = deviceInfo;
    }
    await saveToBackend(payload, appSessionToken);
    // Record a user_info_saved stage for flow tracking
    try {
      saveSkinAnalyticsEvent(
        sessionId,
        {
          event_type: 'user_info_saved',
          event_category: 'user',
          step_name: 'user_info',
          page_name: 'UserInfoPage',
          ...payload,
        },
        appSessionToken
      ).catch(() => {});
    } catch (_) {}
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Save contact + user info directly to backend. Optionally include AQI. No cache.
 * @param {string} sessionId
 * @param {object} contactData - { name, mobileNumber, email, instagramId }
 * @param {object} userInfo - { gender, skinType, age }
 * @param {object} aqiData - optional { success, uv_index, temperature, humidity, wind_speed, location }
 */
export async function saveContactInfo(sessionId, contactData, userInfo = {}, aqiData = null, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const payload = {
      ...toBackendUserContact({ ...userInfo, ...contactData })
    };
    if (aqiData && aqiData.success) {
      payload.uv_index = aqiData.uv_index ?? null;
      payload.temperature = aqiData.temperature ?? null;
      payload.humidity = aqiData.humidity ?? null;
      payload.wind_speed = aqiData.wind_speed ?? null;
      payload.location = aqiData.location ?? null;
      payload.latitude = aqiData.latitude ?? null;
      payload.longitude = aqiData.longitude ?? null;
    }
    // Stage (merge) and let a later flush coalesce this with other updates (AQI, detection, goals).
    _mergeIntoPending(sessionId, payload);
    _scheduleSafetyFlush(sessionId, appSessionToken);
    saveSkinAnalyticsEvent(
      sessionId,
      {
        event_type: 'lead_saved',
        event_category: 'lead',
        step_name: 'contact_info',
        page_name: 'FaceDetectionPage',
        funnel_stage: 'lead_capture',
        is_verified: Boolean(contactData?.otp_verified),
        ...buildEnvironmentalAnalyticsFields(aqiData),
        event_metadata: {
          has_name: Boolean(payload.name),
          has_mobile: Boolean(payload.mobile_number),
          has_email: Boolean(payload.email),
          has_instagram: Boolean(payload.instagram_id),
        },
      },
      appSessionToken
    ).catch(() => {});
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

/**
 * Supplemental profile/contact save after canonical detect persistence (no skin_data).
 */
export async function saveSupplementalProfile(sessionId, userAndContact = {}, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  const sid = String(sessionId).trim();
  const contact = toBackendUserContact(userAndContact);
  const hasFields = Object.values(contact).some((v) => v != null && v !== '');
  if (!hasFields) return { success: true, skipped: true };
  try {
    const pending = _loadPending();
    const staged = (pending[sid] && typeof pending[sid] === 'object') ? pending[sid] : {};
    const { _meta, skin_data, ...stagedRest } = staged;
    const payload = { ...toIdentifier(sid), ...stagedRest, ...contact };
    const result = await saveToBackend(payload, appSessionToken);
    _clearPending(sid);
    return { success: true, supplemental: true, persistenceVerified: result?.persistence_verified };
  } catch (e) {
    logSkinResilience('failed', { label: 'save-analysis-supplemental', sessionId: sid, message: e.message });
    return { success: false, error: e.message, retryable: true };
  }
}

/**
 * Save skin detection results + optional user/contact. No cache.
 * @deprecated Canonical detect persists server-side; prefer saveSupplementalProfile after detect.
 */
export async function saveSkinDetection(sessionId, skinResults, userAndContact = {}, appSessionToken = null) {
  if (sessionId == null || String(sessionId).trim() === '')
    return { success: false, error: 'Session/skin ID is required' };
  try {
    const sid = String(sessionId).trim();
    const analysisVersion = new Date().toISOString();
    const pending = _loadPending();
    const staged = (pending[sid] && typeof pending[sid] === 'object') ? pending[sid] : {};
    const { _meta, ...stagedRest } = staged;
    const payload = {
      ...toIdentifier(sid),
      ...stagedRest,
      skin_data: buildDetectionSkinDataPayload(skinResults),
      analysis_version: analysisVersion,
      ...toBackendUserContact(userAndContact),
    };
    logSkinResilience('save_analysis_flush_started', { sessionId: sid, analysisVersion });
    const result = await saveToBackend(payload, appSessionToken);
    _clearPending(sid);
    logSkinResilience('save_analysis_flush_completed', {
      sessionId: sid,
      analysisVersion,
      persistenceVerified: result?.persistence_verified,
    });
    saveSkinAnalyticsEvent(
      sessionId,
      {
        event_type: 'analysis_completed',
        event_category: 'analysis',
        step_name: 'skin_detection',
        page_name: 'AISkinAnalysisPage',
        funnel_stage: 'analysis',
        event_metadata: {
          detections_count: Array.isArray(skinResults?.detections) ? skinResults.detections.length : 0,
          front_detections_count: Array.isArray(skinResults?.frontDetections) ? skinResults.frontDetections.length : 0,
        },
      },
      appSessionToken
    ).catch(() => {});
    return {
      success: true,
      persisted: true,
      analysisVersion,
      persistenceVerified: result?.persistence_verified,
    };
  } catch (e) {
    logSkinResilience('failed', { label: 'save-analysis', sessionId, message: e.message });
    return { success: false, error: e.message, retryable: true };
  }
}

/**
 * Save skin goals + optional AQI + user/contact/skinData from state. No cache.
 * @param {string} sessionId
 * @param {object} skinGoalData
 * @param {object} aqiData - optional
 * @param {object} options - { userData, skinResults } from location.state
 */
export async function saveSkinGoals(sessionId, skinGoalData, aqiData = null, options = {}, appSessionToken = null) {
  // Skin goals persistence removed for skin flow; keep as no-op to avoid refactor.
  return { success: true, skipped: true };
}

/** Save only AQI/weather update for an existing session. No cache. */
export async function saveAqiUpdate(sessionId, aqiData, appSessionToken = null) {
  if (sessionId == null || !aqiData || !aqiData.success) return { success: false };
  try {
    const payload = {
      uv_index: aqiData.uv_index ?? null,
      temperature: aqiData.temperature ?? null,
      humidity: aqiData.humidity ?? null,
      wind_speed: aqiData.wind_speed ?? null,
      location: aqiData.location ?? null,
      latitude: aqiData.latitude ?? null,
      longitude: aqiData.longitude ?? null
    };
    _mergeIntoPending(sessionId, payload);
    _scheduleSafetyFlush(sessionId, appSessionToken);
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}
